import React, { useState } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Loader2, Receipt } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { amountToCents, formatMoney } from "../../helpers/money";

const inputClass =
  "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all";
const labelClass =
  "text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block";

const emptyLine = () => ({ description: "", amount: "" });

/**
 * Emite un cargo suelto fuera del ciclo mensual: un extra que pidió el cliente.
 * Solo para el dueño de la plataforma.
 */
const ManualInvoiceModal = ({ open, onClose, onCreated, config }) => {
  const [lines, setLines] = useState([emptyLine()]);
  const [dueDays, setDueDays] = useState("");
  const [taxPercent, setTaxPercent] = useState("");
  const [autoCharge, setAutoCharge] = useState(false);
  const [ncf, setNcf] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const currency = config?.currency || "DOP";
  // Cuando el campo queda vacío se usa el valor del plan, igual que en el
  // backend: así el formulario no obliga a repetir lo que ya está configurado.
  const effectiveTaxPercent =
    taxPercent === "" ? (config?.taxBasisPoints || 0) / 100 : Number(taxPercent);

  const subtotalCents = lines.reduce(
    (total, line) => total + amountToCents(line.amount),
    0
  );
  const taxCents = Math.round(
    (subtotalCents * Math.round(effectiveTaxPercent * 100)) / 10000
  );
  const totalCents = subtotalCents + taxCents;

  const reset = () => {
    setLines([emptyLine()]);
    setDueDays("");
    setTaxPercent("");
    setAutoCharge(false);
    setNcf("");
  };

  const patchLine = (index, changes) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...changes } : line))
    );

  const handleSubmit = async () => {
    const items = lines
      .map((line) => ({
        description: line.description.trim(),
        amountCents: amountToCents(line.amount),
      }))
      .filter((item) => item.description || item.amountCents > 0);

    if (items.length === 0) {
      toast.error("Agregá al menos un concepto.");
      return;
    }
    if (items.some((item) => !item.description)) {
      toast.error("Todos los conceptos necesitan una descripción.");
      return;
    }
    if (totalCents <= 0) {
      toast.error("El total tiene que ser mayor que cero.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/billing/invoices/manual", {
        items,
        dueDays: dueDays === "" ? undefined : Number(dueDays),
        taxBasisPoints:
          taxPercent === ""
            ? undefined
            : Math.round(Number(taxPercent) * 100),
        autoCharge,
        ncf: ncf.trim() || undefined,
      });
      toast.success(`Factura ${data.number} emitida`);
      reset();
      if (onCreated) onCreated(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Factura manual
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Un cargo suelto, aparte de la cuota mensual. Se cobra por el mismo
            camino que las demás (el cliente la ve en Facturación y la paga con
            el botón Pagar), pero no cuenta para la suspensión automática del
            acceso.
          </p>

          <div className="space-y-2">
            <label className={labelClass}>Conceptos</label>
            {lines.map((line, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="flex gap-2 items-start">
                <input
                  type="text"
                  placeholder="Descripción (ej. Integración con su CRM)"
                  value={line.description}
                  onChange={(e) =>
                    patchLine(index, { description: e.target.value })
                  }
                  className={inputClass}
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={line.amount}
                  onChange={(e) => patchLine(index, { amount: e.target.value })}
                  className={`${inputClass} max-w-[9rem]`}
                />
                <button
                  type="button"
                  disabled={lines.length === 1}
                  onClick={() =>
                    setLines((prev) => prev.filter((_, i) => i !== index))
                  }
                  className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-30 disabled:hover:bg-transparent"
                  title="Quitar concepto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Plus className="w-4 h-4" />
              Agregar concepto
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Días para vencer</label>
              <input
                type="number"
                min={0}
                max={180}
                placeholder={String(config?.dueDays ?? 5)}
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
                className={inputClass}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Vacío = los del plan.
              </p>
            </div>
            <div>
              <label className={labelClass}>ITBIS (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                placeholder={String((config?.taxBasisPoints || 0) / 100)}
                value={taxPercent}
                onChange={(e) => setTaxPercent(e.target.value)}
                className={inputClass}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Vacío = el del plan.
              </p>
            </div>
            <div>
              <label className={labelClass}>NCF (opcional)</label>
              <input
                type="text"
                value={ncf}
                onChange={(e) => setNcf(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoCharge}
              onChange={(e) => setAutoCharge(e.target.checked)}
              className="mt-0.5 rounded border-slate-300"
            />
            <span>
              Cobrar con el método guardado del cliente
              <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                Desmarcado, el cliente paga este cargo cuando quiera. Marcado,
                se le cobra a la tarjeta ya guardada sin que la apruebe de nuevo.
              </span>
            </span>
          </label>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-1 text-sm">
            <div className="flex justify-between text-slate-500 dark:text-slate-400">
              <span>Subtotal</span>
              <span className="tabular-nums">
                {formatMoney(subtotalCents, currency)}
              </span>
            </div>
            <div className="flex justify-between text-slate-500 dark:text-slate-400">
              <span>ITBIS ({effectiveTaxPercent}%)</span>
              <span className="tabular-nums">
                {formatMoney(taxCents, currency)}
              </span>
            </div>
            <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-100 pt-1 border-t border-slate-100 dark:border-slate-800">
              <span>Total</span>
              <span className="tabular-nums">
                {formatMoney(totalCents, currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || totalCents <= 0}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Receipt className="w-4 h-4" />
            )}
            Emitir factura
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualInvoiceModal;
